import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import Index from "./pages/Index";
import Detect from "./pages/Detect";
import Sources from "./pages/Sources";
import Report from "./pages/Report";
import Learn from "./pages/Learn";
import Tools from "./pages/Tools";
import Community from "./pages/Community";
import NotFound from "./pages/NotFound";
import Register from "./pages/auth/Register";
import SignIn from "./pages/auth/SignIn";
import VerifyEmail from "./pages/auth/VerifyEmail";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import EnhancedDashboard from "./pages/EnhancedDashboard";
import Events from "./pages/Events";
import Experts from "./pages/Experts";
import PrivacyAndHacking from "./pages/PrivacyAndHacking";
import AboutUs from "./pages/AboutUs";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import CyberThreat from "./pages/CyberThreat";
import EnhancedSidebar from "@/components/EnhancedSidebar";
import Navbar from "@/components/Navbar";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MobileMenuProvider, useMobileMenu } from './contexts/MobileMenuContext';
import ProtectedRoute from './components/ProtectedRoute';
import VoiceAgent from "./pages/VoiceAgent";
import { FaBars } from 'react-icons/fa';
import OnboardingFlow from './components/OnboardingFlow';
import { OnboardingHighlightProvider } from './contexts/OnboardingHighlightContext';
import CursorTrail from './components/CursorTrail';

const queryClient = new QueryClient();

const AppContent = () => {
  const { currentUser, loading } = useAuth();
  const { toggleMobileMenu } = useMobileMenu();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (currentUser && !localStorage.getItem(`onboardingCompleted_${currentUser.uid}`)) {
      setShowOnboarding(true);
    }
  }, [currentUser]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (currentUser) {
      localStorage.setItem(`onboardingCompleted_${currentUser.uid}`, 'true');
    }
  };

  return (
    <OnboardingHighlightProvider>
      <CursorTrail />
      {/* Global Mobile Menu Toggle - Fixed at top left */}
      {isMobile && (
        <button
          onClick={toggleMobileMenu}
          className="fixed top-0 left-0 w-12 h-12 flex items-center justify-center bg-black text-cyan-400 hover:text-cyan-300 z-[100] border-r border-b border-cyan-500"
          style={{ margin: 0, padding: 0, boxShadow: '0 0 10px rgba(6, 182, 212, 0.3)' }}
          aria-label="Toggle menu"
        >
          <FaBars className="h-6 w-6" />
        </button>
      )}
      {currentUser && !currentUser.emailVerified && <VerifyEmail />}
      {showOnboarding && <OnboardingFlow onOnboardingComplete={handleOnboardingComplete} />}
      <Routes>
        {/* Public routes */}
        <Route element={<>
          <Navbar />
          <div className="min-h-screen bg-gradient-to-br from-gray-900 to-slate-800 grid-pattern">
            <Outlet />
          </div>
        </>}>
          <Route path="/" element={<Index />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/signin" element={<SignIn />} />
          <Route path="/auth/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Protected routes */}
        <Route element={
          <div className="flex flex-row min-h-screen">
            <EnhancedSidebar />
            <div className="flex-1 min-w-0 md:ml-48 p-6">
              <Outlet />
            </div>
          </div>
        }>
          <Route path="/dashboard" element={<ProtectedRoute children={<EnhancedDashboard />} />} />
          <Route path="/detect" element={<ProtectedRoute children={<Detect />} />} />
          <Route path="/sources" element={<ProtectedRoute children={<Sources />} />} />
          <Route path="/report" element={<ProtectedRoute children={<Report />} />} />
          <Route path="/learn" element={<ProtectedRoute children={<Learn />} />} />
          <Route path="/tools" element={<ProtectedRoute children={<Tools />} />} />
          <Route path="/community" element={<ProtectedRoute children={<Community />} />} />
          <Route path="/events" element={<ProtectedRoute children={<Events />} />} />
          <Route path="/experts" element={<ProtectedRoute children={<Experts />} />} />
          <Route path="/privacy-and-hacking" element={<ProtectedRoute children={<PrivacyAndHacking />} />} />
          <Route path="/voice-agent" element={<ProtectedRoute children={<VoiceAgent />} />} />
          <Route path="/cyber-threat" element={<ProtectedRoute children={<CyberThreat />} />} />
        </Route>

        {/* Catch-all route */}
        <Route path="*" element={<NotFound />} />
        <Route path="/history" element={<History />} />
      </Routes>
    </OnboardingHighlightProvider>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <MobileMenuProvider>
          <LanguageProvider>
            <QueryClientProvider client={queryClient}>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <AppContent />
              </TooltipProvider>
            </QueryClientProvider>
          </LanguageProvider>
        </MobileMenuProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
